import { useMemo } from "react";
import { Column } from "primereact/column";
import localeText from "../../../../locale";
import AdminService from "../../../../service/AdminService";
import { buildSearchText, getToken } from "../../utils/adminUtils";
import CatalogTable from "../../components/list/CatalogTable";
import StatusBadge from "../../components/list/StatusBadge";
import useReport from "./hooks/useReport";
import ReportToolbar from "./components/ReportToolbar";
import ReportSummary from "./components/ReportSummary";
import InvoicingControl from "./components/InvoicingControl";
import InvoiceBadge from "./components/InvoiceBadge";
import { formatMoney, formatReportDate, sumBy, toReportRows, toTimestamp } from "./reportUtils";
import "../AdminHome/AdminHome.css";
import "./AdminReports.css";

// Formas de pagamento de cada pedido; só as usadas aparecem na linha
const PAYMENT_METHODS = [
  { field: "paidPaypal", labelKey: "paidOrdersHeaderPayPal" },
  { field: "paidWallet", labelKey: "paidOrdersHeaderWallet" },
  { field: "paidExtracredit", labelKey: "paidOrdersHeaderExtraCredit" },
  { field: "paidTransferBank", labelKey: "paidOrdersHeaderBankTransfer" },
];

const hasAmount = (value) => value !== undefined && value !== null && value !== "" && Number(value) !== 0;

const loadPaidOrders = async (dateBegin, dateEnd) =>
  toReportRows(await new AdminService().getPaidOrdersReport(getToken(), dateBegin, dateEnd));

const loadPaidOrdersPdf = (dateBegin, dateEnd) =>
  new AdminService().getPaidOrdersReportPdf(getToken(), dateBegin, dateEnd);

const PartyCell = ({ name, taxId, address }) => (
  <div className="admin-stack">
    <span className="admin-strong">{name || "—"}</span>
    {taxId && <span className="admin-muted">CF/P.IVA: {taxId}</span>}
    {address && <span className="admin-muted admin-report__address">{address}</span>}
  </div>
);

const AdminPaidOrdersReport = ({ canManageInvoicing = false }) => {
  const report = useReport({
    load: loadPaidOrders,
    loadPdf: loadPaidOrdersPdf,
    pdfFileName: "paid-orders-report",
  });
  const { rows: rawRows, loading, loadError, reload } = report;

  const rows = useMemo(
    () =>
      rawRows.map((row, index) => ({
        ...row,
        id: row.orderId ?? `row-${index}`,
        dateValue: toTimestamp(row.date),
        searchText: buildSearchText([
          String(row.orderId ?? ""),
          row.buyer,
          row.seller,
          row.partitaivaBuyer,
          row.partitaivaSeller,
          row.status,
        ]),
      })),
    [rawRows],
  );

  const onHold = rows.filter((row) => row.amountToSeller === null || row.amountToSeller === undefined);

  const summary = [
    { label: localeText("adminReportsOrdersCount"), value: rows.length.toLocaleString() },
    {
      label: localeText("paidOrdersHeaderOrderAmount"),
      value: formatMoney(sumBy(rows, (row) => row.amountOrder)),
    },
    {
      label: localeText("paidOrdersHeaderPaidAmount"),
      value: formatMoney(sumBy(rows, (row) => row.amountPaid)),
    },
    {
      label: localeText("adminReportsPlatformRevenue"),
      value: formatMoney(sumBy(rows, (row) => row.amountToPlatform)),
    },
    {
      label: localeText("paidOrdersHeaderPaidToSeller"),
      value: formatMoney(sumBy(rows, (row) => row.amountToSeller)),
      hint: onHold.length
        ? `${onHold.length} ${localeText("adminReportsOnHoldCount")}`
        : null,
    },
    { label: "Cashback", value: formatMoney(sumBy(rows, (row) => row.cashback)) },
  ];

  return (
    <div className="admin-list admin-report">
      <ReportToolbar report={report} />
      {canManageInvoicing && <InvoicingControl />}
      <ReportSummary items={summary} loading={loading} />

      <CatalogTable
        rows={rows}
        loading={loading}
        loadError={loadError}
        onReload={reload}
        searchPlaceholder={localeText("adminReportsSearchOrders")}
        sortField="dateValue"
        sortOrder={-1}
        minWidth="84rem"
      >
        <Column
          header={localeText("paidOrdersHeaderNumber")}
          field="orderId"
          body={(row) => (
            <div className="admin-stack">
              <span className="admin-strong">#{row.orderId ?? "—"}</span>
              <span className="admin-muted">{formatReportDate(row.date)}</span>
            </div>
          )}
          sortable
          sortField="dateValue"
        />
        <Column
          header={localeText("paidOrdersHeaderBuyer")}
          field="buyer"
          body={(row) => (
            <PartyCell name={row.buyer} taxId={row.partitaivaBuyer} address={row.addressBuyer} />
          )}
          sortable
          style={{ minWidth: "14rem" }}
        />
        <Column
          header={localeText("paidOrdersHeaderSeller")}
          field="seller"
          body={(row) => (
            <PartyCell name={row.seller} taxId={row.partitaivaSeller} address={row.addressSeller} />
          )}
          sortable
          style={{ minWidth: "14rem" }}
        />
        <Column
          header={localeText("paidOrdersHeaderPaidAmount")}
          field="amountPaid"
          body={(row) => (
            <div className="admin-stack">
              <span className="admin-num admin-strong">{formatMoney(row.amountPaid)}</span>
              <span className="admin-num admin-muted">
                {localeText("adminReportsOrderShort")} {formatMoney(row.amountOrder)}
              </span>
            </div>
          )}
          sortable
        />
        <Column
          header={localeText("adminReportsPaymentMethods")}
          body={(row) => {
            const used = PAYMENT_METHODS.filter((method) => hasAmount(row[method.field]));
            return used.length ? (
              <div className="admin-stack">
                {used.map((method) => (
                  <span key={method.field} className="admin-num">
                    <span className="admin-muted">{localeText(method.labelKey)}</span>{" "}
                    {formatMoney(row[method.field])}
                  </span>
                ))}
              </div>
            ) : (
              <span className="admin-muted">—</span>
            );
          }}
        />
        {/* No clássico, comissão e as duas taxas da plataforma exibiam o mesmo campo */}
        <Column
          header={localeText("adminReportsPlatformRevenue")}
          field="amountToPlatform"
          body={(row) => <span className="admin-num">{formatMoney(row.amountToPlatform)}</span>}
          sortable
        />
        <Column
          header={localeText("paidOrdersHeaderPaidToSeller")}
          field="amountToSeller"
          body={(row) =>
            row.amountToSeller === null || row.amountToSeller === undefined ? (
              <StatusBadge tone="warning">{localeText("paidOrdersStatusOnHold")}</StatusBadge>
            ) : (
              <span className="admin-num">{formatMoney(row.amountToSeller)}</span>
            )
          }
          sortable
        />
        <Column
          header="Cashback"
          field="cashback"
          body={(row) => <span className="admin-num">{formatMoney(row.cashback)}</span>}
          sortable
        />
        <Column
          header={localeText("paidOrdersHeaderStatus")}
          field="status"
          body={(row) => row.status || "—"}
          sortable
        />
        <Column
          header={localeText("adminReportsInvoices")}
          body={(row) => (
            <div className="admin-report__invoices">
              <span className="admin-muted">{localeText("adminReportsBuyerShort")}</span>
              <InvoiceBadge
                status={row.invoiceStatusBuyer}
                number={row.invoiceNumberBuyer}
                year={row.invoiceYearBuyer}
                errorMessage={row.invoiceErrorMessageBuyer}
              />
              <span className="admin-muted">{localeText("adminReportsSellerShort")}</span>
              <InvoiceBadge
                status={row.invoiceStatusSeller}
                number={row.invoiceNumberSeller}
                year={row.invoiceYearSeller}
                errorMessage={row.invoiceErrorMessageSeller}
              />
            </div>
          )}
        />
      </CatalogTable>
    </div>
  );
};

export default AdminPaidOrdersReport;
